import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen, FileArchive, FileSpreadsheet, Download, UploadCloud,
  CheckCircle2, AlertTriangle, RotateCcw, ShieldCheck,
} from "lucide-react";

/**
 * Import dei LIBRI MANUTENZIONE in Word, in tre momenti:
 *
 *   1. si carica lo ZIP delle schede e l'app restituisce un riepilogo Excel
 *      gia' compilato, segnalando in giallo cio' che non e' riuscita a leggere;
 *   2. si corregge il riepilogo a mano e lo si ricarica per una verifica, che
 *      dice cosa cambierebbe senza salvare niente;
 *   3. si conferma, e le schede delle attrezzature vengono create o aggiornate.
 */
export default function ImportLibriManutenzione({ onDone }) {
  const zipRef = useRef(null);
  const xlsRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zip, setZip] = useState(null);
  const [xls, setXls] = useState(null);
  const [analisi, setAnalisi] = useState(null);
  const [verifica, setVerifica] = useState(null);
  const [esito, setEsito] = useState(null);
  const [sostituisciStorico, setSostituisciStorico] = useState(true);

  const scarica = (base64, nome) => {
    const binario = atob(base64);
    const bytes = new Uint8Array(binario.length);
    for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
    const url = window.URL.createObjectURL(new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }));
    const a = document.createElement("a");
    a.href = url;
    a.download = nome;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const analizza = async () => {
    if (!zip) { toast.error("Seleziona lo ZIP con le schede Word"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("zip_file", zip);
    try {
      const { data } = await api.post("/equipment/libri-manutenzione/analizza", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setAnalisi(data);
      scarica(data.file_base64, data.nome_file);
      toast.success(`${data.schede_lette} schede lette — riepilogo scaricato`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante la lettura delle schede");
    } finally { setBusy(false); }
  };

  const invia = async (soloVerifica) => {
    if (!xls) { toast.error("Seleziona il riepilogo Excel corretto"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("excel_file", xls);
    // Lo ZIP torna utile solo per riprendere le foto delle macchine.
    if (zip && !soloVerifica) fd.append("zip_file", zip);
    fd.append("solo_verifica", soloVerifica ? "true" : "false");
    fd.append("sostituisci_storico", sostituisciStorico ? "true" : "false");
    try {
      const { data } = await api.post("/equipment/libri-manutenzione/applica", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (soloVerifica) {
        setVerifica(data);
        toast.success("Verifica completata — niente e' stato salvato");
      } else {
        setEsito(data);
        toast.success(`${data.creati} schede create, ${data.aggiornati} aggiornate`);
        onDone?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'importazione");
    } finally { setBusy(false); }
  };

  const azzera = () => {
    setZip(null); setXls(null); setAnalisi(null); setVerifica(null); setEsito(null);
  };
  const chiudi = () => { setOpen(false); azzera(); };

  const report = esito || verifica;

  return (<>
    <Button variant="outline" className="h-12" onClick={() => setOpen(true)}
            data-testid="import-libri-btn">
      <BookOpen className="w-4 h-4 mr-2" /> Libri manutenzione
    </Button>

    {open && (
      <Dialog open onOpenChange={chiudi}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import dei libri di manutenzione</DialogTitle>
          </DialogHeader>

          {!report && (
            <div className="space-y-4">
              {/* --- passo 1 --------------------------------------------- */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
                <div className="text-sm font-semibold">1 · Carica le schede Word</div>
                <p className="text-xs text-slate-600">
                  Metti in uno ZIP le cartelle dei libri di manutenzione, così come sono
                  sul server. L'app le legge tutte e ti restituisce un riepilogo Excel già
                  compilato. In questo passaggio non viene salvato niente.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Archivio ZIP</Label>
                    <input ref={zipRef} type="file" accept=".zip" hidden
                           onChange={(e) => setZip(e.target.files?.[0] || null)} />
                    <Button variant="outline" className="w-full mt-1 justify-start"
                            onClick={() => zipRef.current?.click()}>
                      <FileArchive className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{zip ? zip.name : "Seleziona ZIP..."}</span>
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                            disabled={busy || !zip} onClick={analizza}>
                      <Download className="w-4 h-4 mr-2" />
                      {busy && !analisi ? "Lettura in corso..." : "Leggi e scarica il riepilogo"}
                    </Button>
                  </div>
                </div>

                {analisi && (
                  <div className="space-y-2 pt-1">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <Cifra etichetta="Schede lette" valore={analisi.schede_lette} tono="ok" />
                      <Cifra etichetta="Nuove" valore={analisi.nuove} tono="neutro" />
                      <Cifra etichetta="Già presenti" valore={analisi.gia_presenti} tono="neutro" />
                      <Cifra etichetta="Da controllare" valore={analisi.da_controllare}
                             tono={analisi.da_controllare ? "attenzione" : "neutro"} />
                    </div>
                    <p className="text-xs text-slate-600">
                      {analisi.righe_storico} righe di storico e {analisi.con_foto} foto trovate.
                      Nel riepilogo, la colonna <b>da_controllare</b> in giallo dice riga per riga
                      cosa non è stato possibile leggere: correggi lì e ricarica il file qui sotto.
                    </p>
                    {analisi.scartati?.length > 0 && (
                      <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 space-y-1">
                        <div className="font-semibold text-amber-800">
                          {analisi.scartati.length} file non riconosciuti come libri di manutenzione:
                        </div>
                        {analisi.scartati.slice(0, 6).map((s, i) => (
                          <div key={i} className="text-amber-800 font-mono truncate">{s.file}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* --- passo 2 --------------------------------------------- */}
              <div className={`p-3 border rounded space-y-2 ${analisi ? "bg-slate-50 border-slate-200" : "bg-slate-50/50 border-slate-100 opacity-60"}`}>
                <div className="text-sm font-semibold">2 · Ricarica il riepilogo corretto</div>
                <p className="text-xs text-slate-600">
                  Metti <b>NO</b> nella colonna <b>importa</b> per saltare una macchina.
                  Prima di salvare vedrai un riepilogo di cosa cambierà.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Riepilogo .xlsx</Label>
                    <input ref={xlsRef} type="file" accept=".xlsx,.xlsm" hidden
                           onChange={(e) => setXls(e.target.files?.[0] || null)} />
                    <Button variant="outline" className="w-full mt-1 justify-start"
                            onClick={() => xlsRef.current?.click()}>
                      <FileSpreadsheet className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{xls ? xls.name : "Seleziona Excel..."}</span>
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <Button variant="outline" className="w-full" disabled={busy || !xls}
                            onClick={() => invia(true)}>
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      {busy ? "Verifica in corso..." : "Verifica senza salvare"}
                    </Button>
                  </div>
                </div>
                <div className="flex items-start gap-2 pt-1">
                  <input id="sost-storico" type="checkbox" className="mt-1"
                         checked={sostituisciStorico}
                         onChange={(e) => setSostituisciStorico(e.target.checked)} />
                  <label htmlFor="sost-storico" className="text-xs text-slate-600">
                    Rifai lo storico importato dai libri invece di aggiungerlo a quello già presente
                    (consigliato: evita interventi doppi se ricarichi il file più volte).
                    Le manutenzioni inserite a mano nell'app non vengono toccate.
                  </label>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={chiudi}>Chiudi</Button>
              </DialogFooter>
            </div>
          )}

          {report && (
            <div className="space-y-4">
              <div className={`text-xs rounded p-2 border ${esito
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-blue-50 border-blue-200 text-blue-800"}`}>
                {esito
                  ? "Importazione eseguita: le schede sono state salvate."
                  : "Questa è solo una verifica — nessuna modifica è stata salvata."}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Cifra etichetta="Schede create" valore={report.creati} tono="ok" />
                <Cifra etichetta="Schede aggiornate" valore={report.aggiornati} tono="neutro" />
                <Cifra etichetta="Righe di storico" valore={report.storico} tono="neutro" />
                <Cifra etichetta="Righe saltate" valore={report.errori_totali}
                       tono={report.errori_totali ? "attenzione" : "neutro"} />
              </div>
              <p className="text-xs text-slate-600">
                {report.controlli} voci di checklist e {report.scadenze} scadenze esterne
                {esito ? ` · ${report.foto} foto salvate` : ""}
                {esito && report.foto_tenute > 0
                  ? ` · ${report.foto_tenute} macchine avevano già una foto e l'hanno tenuta`
                  : ""}.
              </p>

              {report.errori?.length > 0 && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 max-h-40 overflow-y-auto space-y-1">
                  <div className="font-semibold text-amber-800 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Righe non importate
                  </div>
                  {report.errori.map((e, i) => (
                    <div key={i} className="text-amber-800">{e}</div>
                  ))}
                </div>
              )}

              <div className="border border-slate-200 rounded overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-xs min-w-[600px]">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                    <tr>
                      <th className="text-left p-2">Identificativo</th>
                      <th className="text-left p-2">Esito</th>
                      <th className="text-left p-2">Categoria</th>
                      <th className="text-right p-2">Controlli</th>
                      <th className="text-right p-2">Scadenze</th>
                      <th className="text-right p-2">Storico</th>
                      <th className="text-center p-2">Foto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.dettagli.map((d, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-2 font-mono">{d.identificativo}</td>
                        <td className="p-2">
                          <Badge className={`text-[10px] border-0 ${d.stato === "creata"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-blue-100 text-blue-700"}`}>
                            {d.stato}
                          </Badge>
                        </td>
                        <td className="p-2">{d.categoria || "—"}</td>
                        <td className="p-2 text-right">{d.controlli}</td>
                        <td className="p-2 text-right">{d.scadenze}</td>
                        <td className="p-2 text-right">{d.storico}</td>
                        <td className="p-2 text-center">
                          {d.foto ? <CheckCircle2 className="w-3 h-3 text-emerald-600 inline" /> : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={azzera}>
                  <RotateCcw className="w-4 h-4 mr-2" /> Ricomincia
                </Button>
                {!esito && (
                  <Button disabled={busy} onClick={() => invia(false)}
                          className="bg-orange-500 hover:bg-orange-600 text-white">
                    <UploadCloud className="w-4 h-4 mr-2" />
                    {busy ? "Salvataggio..." : `Salva ${report.creati + report.aggiornati} schede`}
                  </Button>
                )}
                {esito && (
                  <Button onClick={chiudi} className="bg-orange-500 hover:bg-orange-600 text-white">
                    Chiudi
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    )}
  </>);
}

function Cifra({ etichetta, valore, tono }) {
  const toni = {
    ok: "border-emerald-200 bg-emerald-50 text-emerald-700",
    attenzione: "border-amber-200 bg-amber-50 text-amber-700",
    neutro: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div className={`border rounded p-2 text-center ${toni[tono] || toni.neutro}`}>
      <div className="text-lg font-bold leading-none">{valore ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide mt-1">{etichetta}</div>
    </div>
  );
}
